import React, { useState } from 'react';
import { FaEdit, FaTrash, FaSearch, FaImage, FaPlus, FaTag, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import Pagination from '../components/Pagination';
import styles from '../styles/ProductList.module.css';
import { formatPrice } from "../utils/formatPrice";

const ProductList = ({
  products,
  filteredProducts,
  selectedProducts,
  searchTerm,
  currentProductPage,
  itemsPerProductPage,
  getCategoryPath,
  categories,
  onSearchChange,
  toggleProductSelection,
  toggleSelectAll,
  onPageChange,
  onEditProduct,
  onDeleteProduct,
  onAddProduct,
  onBulkSale,
  onToggleSaleCarousel,
  isSaleCarouselActive,
  isLoading,
}) => {
  const [expandedAttributes, setExpandedAttributes] = useState({});
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const toggleAttributesExpand = (productId) => {
    setExpandedAttributes(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  // Handle both regular products and EWE products
  const currentProducts = (filteredProducts.length > 0 ? filteredProducts : products).slice(
    (currentProductPage - 1) * itemsPerProductPage,
    currentProductPage * itemsPerProductPage
  );

  // Function to get the correct image URL for EWE products
  const getEweProductImage = (product) => {
    const imageSources = [
      ...(product.urlImages || []).map(img => img.url),
      ...(product.allMedia || []).map(img => img.url),
      `https://resource.ewe.rs/products/${product.acProduct}_v.jpg`,
      `https://resource.ewe.rs/products/${product.acProduct}_1.jpg`
    ];

    const validImage = imageSources.find(src => {
      try {
        if (!src) return false;
        const url = new URL(src);
        return url.protocol.startsWith('http');
      } catch {
        return false;
      }
    });

    return validImage || '/placeholder-product.jpg';
  };

  const getProductImage = (product) => {
    if (product.imageUrls?.length > 0) {
      return product.imageUrls[0];
    }
    return '/noImage.jpg';
  };

  // Function to get category path for EWE products
  const getEweCategoryPath = (product) => {
    if (product.acMainCategory) {
      return `${product.acMainCategory}${product.acCategory ? ` > ${product.acCategory}` : ''}${product.acSubCategory ? ` > ${product.acSubCategory}` : ''}`;
    }
    return 'No category';
  };

  // Function to render attributes for EWE products
  const renderEweAttributes = (product) => {
    if (!product.specification || product.specification.length === 0) {
      return '-';
    }

    const isExpanded = expandedAttributes[product.anProductKey];
    const specsToShow = isExpanded ? product.specification : product.specification.slice(0, 3);
    const hasMoreSpecs = product.specification.length > 3;

    return (
      <div className={styles.attributesContainer}>
        <div className={styles.attributesList}>
          {specsToShow.map((specGroup, groupIndex) => (
            specGroup.filters.map((filter, filterIndex) => (
              <div key={`${groupIndex}-${filterIndex}`} className={styles.attributeItem}>
                {`${filter.acFilterSet}: ${filter.filters}`}
              </div>
            ))
          ))}
        </div>

        {hasMoreSpecs && (
          <button
            className={styles.viewMoreButton}
            onClick={(e) => {
              e.stopPropagation();
              toggleAttributesExpand(product.anProductKey);
            }}
          >
            {isExpanded ? (
              <>Show Less <FaChevronUp className={styles.expandIcon} /></>
            ) : (
              <>View More ({product.specification.length - 3}) <FaChevronDown className={styles.expandIcon} /></>
            )}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className={styles.productListContainer}>
      <div className={styles.listControls}>
        {/* ... (keep your existing controls) ... */}
      </div>

      <div className={styles.productTableContainer}>
        {isLoading && (
          <div className={styles.loadingOverlay}>
            <div className={styles.loadingSpinner}></div>
          </div>
        )}

        <table className={styles.productsTable}>
          <thead>
            <tr>
              <th>Slika</th>
              <th>Ime</th>
              <th>Brand</th>
              <th>Serijski Broj</th>
              <th>Kategorija</th>
              <th>Količina</th>
              <th>Nabavna Cena</th>
              <th>Maloprodajna Cena </th>
              <th>Popust</th>
            </tr>
          </thead>
          <tbody>
            {currentProducts.map((product) => {
              const isOnSale = product.isPromotion === 1 && product.promoPrice;
              const displayPrice = isOnSale ? product.promoPrice : product.anPrice;

              return (
                <tr key={product.id || product.anProductKey}>
                  <td className={styles.productImageCell}>
                    <img
                      src={getProductImage(product)}
                      alt={product.acName || product.name}
                      className={styles.productImage}
                      onError={(e) => {
                        e.target.src = '/noImage.jpg';
                        e.target.classList.add(styles.imageError);
                      }}
                    />
                  </td>
                  <td>{product.acName || product.name}</td>
                  <td>{product.acDept || product.brand || '-'}</td>
                  <td>{product.acProduct || product.productCode || '-'}</td>
                  <td>
                    {product.category?.id
                      ? getCategoryPath(product.category.id, categories)
                      : getEweCategoryPath(product)}
                  </td>
                  <td>
                    <span className={`${styles.stockIndicator} ${(product.anStock || product.stock) < 10 ? styles.stockLow : styles.stockOk}`}>
                      {product.anStock || product.stock || 0} in stock
                    </span>
                  </td>
                  <td>
                    {isOnSale ? (
                      <>
                        <span style={{ textDecoration: 'line-through' }}>
                          {formatPrice(product.anPrice || product.price || 0)} RSD
                        </span>
                        <span style={{ color: 'red', marginLeft: '8px' }}>
                          {formatPrice(product.promoPrice || 0)} RSD
                        </span>
                      </>
                    ) : (
                      <span>{formatPrice(product.anPrice || product.price || 0)} RSD</span>
                    )}
                  </td>
                  <td>{formatPrice(product.anRetailPRice || product.retailPrice || 0)} RSD</td>
                  <td>{product.isPromotion === 1 ? `${product.anDiscount || 0}% OFF` : '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentProductPage}
        totalPages={Math.ceil((filteredProducts.length > 0 ? filteredProducts : products).length / itemsPerProductPage)}
        onPageChange={onPageChange}
      />
    </div>
  );
};

export default ProductList;